import * as React from "react";
import { useState } from "react";
import { QualifiedFeatureFragment } from "fe-common/graphql";
import { ACTION, CATEGORY, evt } from "fe-common/analytics/analytics";
import { PatientChartFinding } from "./PatientChartFinding";
import { KahunPanelSubGroup, Relations, Values } from "shared";
import styled from "@mui/material/styles/styled";
import ListItem from "@mui/material/ListItem";
import { RosebudColorMap } from "../rosebud/rosebudColorMap";
import { groupBy } from "fe-common/ui/arrayUtils";
import { getContainedSubGroup } from "../config/dataInputSectionConfiguration";
import { getPatientFindingsSection, usePatientCase } from "../usePatientCase";
import { PatientCaseGrouping } from "../../../context/diagnosticAgent";

//region [[ Styles ]]

const ShowAllButton = styled(ListItem)({
  alignItems: "center",
  textDecoration: "underline",
  fontSize: "16px",
  color: RosebudColorMap.gray2,
});

//endregion [[ Styles ]]

//region [[ Props ]]

export interface PatientChartVitalSignsPanelProps {
  gotoSection: (subGroupId) => void;
  remove: (finding: any) => void;
  onRefine: (e: any, qfId: any) => void;
  subGroupId: string;
}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

export const PatientChartVitalSignsFindings = ({
  ...props
}: PatientChartVitalSignsPanelProps) => {
  const { patientCase } = usePatientCase();

  const qfs = getPatientFindingsSection(
    PatientCaseGrouping.CLINICAL_FEATURES,
    patientCase,
  ).filter((qf) => !qf.skip);
  const qfsGrouped = groupBy(
    qfs,
    (qf) => getContainedSubGroup(qf, PatientCaseGrouping.CLINICAL_FEATURES).id,
  );

  const vitalSignsFindings = qfsGrouped[KahunPanelSubGroup.VitalSign];

  const vitalSignsAbnormal = vitalSignsFindings
    ? vitalSignsFindings.filter(
        (finding) =>
          finding.qualifiers.filter(
            (q) =>
              q.relation.id === Relations.LEVEL && q.value.id !== Values.NORMAL,
          ).length > 0,
      )
    : [];

  const vitalSignsNormal = vitalSignsFindings
    ? vitalSignsFindings.filter(
        (finding) =>
          finding.qualifiers.filter(
            (q) =>
              q.relation.id === Relations.LEVEL && q.value.id === Values.NORMAL,
          ).length > 0,
      )
    : [];

  const [showMore, setShowMore] = useState(false);

  const onShowAll = () => {
    setShowMore(!showMore);
  };

  const handleDelete = (qf: QualifiedFeatureFragment) => {
    evt({
      category: CATEGORY.AGENT_PATIENT_VIEW,
      action: ACTION.REMOVE_FINDING_CHIP,
      label: qf.name,
    });
    props.remove(qf);
  };

  return (
    <>
      {vitalSignsAbnormal.map((qf, index) => (
        <PatientChartFinding
          key={index}
          qf={qf}
          gotoSection={props.gotoSection}
          index={index}
          isEditable={props.subGroupId === KahunPanelSubGroup.SignSymptom}
          onDelete={() => {
            handleDelete(qf);
          }}
        />
      ))}

      {showMore &&
        vitalSignsNormal.map((qf, index) => (
          <PatientChartFinding
            key={index}
            qf={qf}
            gotoSection={props.gotoSection}
            index={index}
            isEditable={props.subGroupId === KahunPanelSubGroup.SignSymptom}
            onDelete={() => {
              handleDelete(qf);
            }}
          />
        ))}

      <ShowAllButton onClick={onShowAll}>
        {!showMore ? "Show All" : "Show Only Abnormal"}
      </ShowAllButton>
    </>
  );
};
